import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Swiper from 'react-id-swiper'
import { connect } from 'react-redux'
import ICONS, { ICON_SIZE } from 'components/common/icon/const'
import Icon from 'components/common/icon'
import SlideContentMedia from './slide-content-media/SlideContentMedia'

const SlideContent = ({ isPhone, homePageBanners }) => {
  const swiperRef = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const isFirstSlideVideo =
    homePageBanners?.[0]?.attributes?.media?.kind === 'video' || false

  const handleSlideChange = useCallback(() => {
    const { realIndex, autoplay } = swiperRef.current.swiper
    setCurrentIndex(realIndex)
    const media = homePageBanners?.[realIndex]?.attributes?.media
    const isVideo = media?.kind === 'video'

    if (isVideo) {
      autoplay.stop()
    } else {
      autoplay.start()
    }
  }, [homePageBanners])

  const goToSlide = (index) => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideTo(index)
    }
  }

  const handleNext = (idx) => {
    const isLastSlide = idx === homePageBanners.length - 1
    if (isLastSlide) {
      goToSlide(0)
      return
    }

    if (swiperRef.current) {
      swiperRef.current.swiper.slideNext()
    }
  }

  const renderIcon = (alt, className, src) => (
    <Icon
      className={className}
      src={ICONS[src]}
      size={ICON_SIZE.medium}
      alt={alt}
    />
  )

  const sliderOptions = {
    slidesPerView: 1,
    loop: false,
    spaceBetween: 0,
    shouldSwiperUpdate: true,
    noSwiping: false,
    simulateTouch: true,
    preventClicks: true,
    preventClicksPropagation: true,
    touchRatio: 0.6,
    threshold: 25,
    autoplay: {
      delay: Number(homePageBanners?.[0]?.auto_slide_ms) ?? 7000,
      disableOnInteraction: false,
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets',
      clickable: true,
    },
    ...(!isPhone && {
      renderPrevButton: () =>
        renderIcon('swiperPrevButton', 'swiper-button-prev', 'arrowHeadLeft'),
      renderNextButton: () =>
        renderIcon('swiperNextButton', 'swiper-button-next', 'arrowHeadRight'),
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
    }),
    on: {
      realIndexChange: () => handleSlideChange(),
      touchEnd: () => {
        const { activeIndex, touches } = swiperRef?.current?.swiper || {}
        const isLastSlide = activeIndex === homePageBanners.length - 1
        const isSwipeRight = touches.diff < -200

        if (isLastSlide && isSwipeRight) {
          setTimeout(() => {
            goToSlide(0)
          }, 100)
        }
      },
    },
  }

  useEffect(() => {
    if (isFirstSlideVideo) {
      handleSlideChange()
    }
  }, [isFirstSlideVideo, handleSlideChange])

  return (
    <Swiper {...sliderOptions} ref={swiperRef}>
      {homePageBanners.map((banner, idx) => {
        const isAutoPlayVideo = currentIndex === idx
        const isLoopVideo = homePageBanners?.length === 1

        return (
          <SlideContentMedia
            key={idx}
            idx={idx}
            banner={banner}
            isPhone={isPhone}
            isLoopVideo={isLoopVideo}
            onHandleNext={handleNext}
            isAutoPlayVideo={isAutoPlayVideo}
          />
        )
      })}
    </Swiper>
  )
}

SlideContent.propTypes = {
  isPhone: PropTypes.bool.isRequired,
  homePageBanners: PropTypes.arrayOf(
    PropTypes.shape({
      auto_slide_ms: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      attributes: PropTypes.shape({
        media: PropTypes.shape({ kind: PropTypes.string }),
      }),
    }),
  ),
}

SlideContent.defaultProps = {
  homePageBanners: [],
}

export default connect((state) => ({
  isPhone: state.device.isPhone,
  slides: state.editorialSlides.payload,
  homePageBanners: state.homePage.banners,
}))(SlideContent)
